/**
 * URL state: `?d=<designId>&v=<version>&p=<base64url json>` so any
 * configuration is linkable before the user signs in.
 */

function toBase64Url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(s: string): string {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/** Only the params that differ from the defaults, to keep links short. */
export function encodeParams(params: Record<string, unknown>, defaults: Record<string, unknown> = {}): string | undefined {
  const diff: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) if (defaults[k] !== v) diff[k] = v;
  if (!Object.keys(diff).length) return undefined;
  return toBase64Url(JSON.stringify(diff));
}

export function decodeParams(p: string | null | undefined): Record<string, unknown> {
  if (!p) return {};
  try {
    const obj = JSON.parse(fromBase64Url(p)) as unknown;
    return obj && typeof obj === "object" && !Array.isArray(obj) ? (obj as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export interface ConfiguratorUrlState {
  designId?: string;
  version?: string;
  params: Record<string, unknown>;
  configId?: string;
  /** Community entry to apply on open (`?community=<id>`). */
  communityId?: string;
  /** Fit ref to open the community tab filtered to (`?fit=<ref>`, from the home page set search). */
  fit?: string;
}

export function parseConfiguratorUrl(search: string): ConfiguratorUrlState {
  const q = new URLSearchParams(search);
  return {
    designId: q.get("d") ?? undefined,
    version: q.get("v") ?? undefined,
    params: decodeParams(q.get("p")),
    configId: q.get("c") ?? undefined,
    communityId: q.get("community") ?? undefined,
    fit: q.get("fit") ?? undefined,
  };
}

export function buildConfiguratorSearch(state: ConfiguratorUrlState, defaults: Record<string, unknown> = {}): string {
  const q = new URLSearchParams();
  if (state.designId) q.set("d", state.designId);
  if (state.version) q.set("v", state.version);
  const p = encodeParams(state.params, defaults);
  if (p) q.set("p", p);
  if (state.configId) q.set("c", state.configId);
  if (state.communityId) q.set("community", state.communityId);
  if (state.fit) q.set("fit", state.fit);
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** Public `/share/<token>` URL for a share token. */
export function shareUrlFor(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, "")}/share/${encodeURIComponent(token)}`;
}

/**
 * The link to copy for "Share": a fresh `/share/<token>` link when a saved config can be
 * shared (`createToken`), otherwise the current configurator URL (params are in the query)
 * without `c=`: a saved config or device draft only opens for its owner, so the link
 * carries the params alone. Returns the URL actually produced by this call, never a previous one.
 */
export async function resolveShareUrl(opts: { origin: string; currentHref: string; createToken?: () => Promise<string> }): Promise<{ url: string; kind: "token" | "params" }> {
  if (opts.createToken) return { url: shareUrlFor(opts.origin, await opts.createToken()), kind: "token" };
  const url = new URL(opts.currentHref);
  url.searchParams.delete("c");
  return { url: url.toString(), kind: "params" };
}
